import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';

// Define DUNA validation status (based on last year)
export type DunaValidationStatus =
  | 'valid'
  | 'attention'
  | 'no_data'
  | 'no_paths';

@Component({
  selector: 'app-duna-validation-indicator',
  standalone: true,
  imports: [CommonModule, MatTooltipModule, MatIconModule],
  template: `
    <mat-icon
      class="validation-indicator"
      [ngClass]="status"
      [matTooltip]="getValidationTooltip()"
      matTooltipPosition="above"
    >
      {{ getValidationIcon() }}
    </mat-icon>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      .validation-indicator {
        position: absolute;
        top: -4px;
        left: -4px;
        width: 16px;
        height: 16px;
        font-size: 10px;
        border-radius: 50%;
        z-index: 10;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        border: 1.5px solid white;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
      }

      .validation-indicator.valid {
        background-color: #4caf50;
      }

      .validation-indicator.attention {
        background-color: #ff9800;
      }

      .validation-indicator.no_data {
        background-color: #2196f3;
      }

      .validation-indicator.no_paths {
        background-color: #9e9e9e;
      }
    `,
  ],
})
export class DunaValidationIndicatorComponent {
  @Input() status: DunaValidationStatus = 'attention';
  @Input() customTooltip?: string;

  /**
   * Gets icon for DUNA validation status
   */
  getValidationIcon(): string {
    switch (this.status) {
      case 'valid':
        return 'check_circle';
      case 'attention':
        return 'warning';
      case 'no_data':
        return 'info';
      case 'no_paths':
        return 'error';
      default:
        return 'warning';
    }
  }

  /**
   * Gets tooltip text for DUNA validation status
   */
  getValidationTooltip(): string {
    if (this.customTooltip) {
      return this.customTooltip;
    }

    const lastYear = new Date().getFullYear() - 1;

    switch (this.status) {
      case 'valid':
        return `DUNA Gyldig - Uddannelsesstien er gyldig baseret på deltagelse i ${lastYear}`;
      case 'attention':
        return `DUNA Opmærksom - Uddannelsesstien kræver opmærksomhed, tjek venligst deltagelse og moduler for ${lastYear}`;
      case 'no_data':
        return `DUNA Ny - Ingen data tilgængelig for ${lastYear}, dette er normalt for nye brugere`;
      case 'no_paths':
        return `DUNA Ingen uddannelsesveje - Ingen gyldige uddannelsesveje tilgængelige for ${lastYear}`;
      default:
        return `DUNA Opmærksom - Uddannelsesstien kræver opmærksomhed`;
    }
  }
}
